import React from 'react';
import { Box, BoxProps } from '../../../CommonComponents/OPL/Box/Box';
import { IconButton } from '../../../CommonComponents/OPL/IconButton/IconButton';
import { ListItem } from '../../../CommonComponents/OPL/List/ListItem';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../CommonComponents/OPL/Menu/Menu';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../CommonComponents/OPL/Text/TruncatedText';
import {
  ColumnWidths,
  EntityDTO,
  EntityTypeDTO,
  ExternalFolder,
  ProviderType,
} from '../../DocumentVault.meta';
import { formatFileSize } from '../../../CommonUtils/formatNumber';
import { EntityIcon } from '../EntityIcon/EntityIcon';
import { ClickableEntityStyle } from './Entity.styles';
import { useIsDocumentVaultReadOnly } from '../../hooks/useIsDocumentVaultReadOnly';
import { useHasAccessToThirdPartyShare } from '../../hooks/useHasAccessToThirdPartyShare';
import { useFeatureFlag } from '../../../_hooks/useFeatureFlag';
import { Feature } from '../../../CommonSelectors/CommonArea.types';

interface Props extends BoxProps {
  entity: EntityDTO;
  onEntityClick(): void;
  onEntityDelete(): void;
  onOrganizeClick(): void;
  onRenameClick(): void;
  onEntityDownload(): void;
  onThirdPartyShareClick?(): void;
}

export const Entity: React.FC<Props> = ({
  entity,
  onEntityClick,
  onEntityDelete,
  onOrganizeClick,
  onRenameClick,
  onEntityDownload,
  onThirdPartyShareClick,
  ...rest
}) => {
  const isDocumentVaultReadOnly = useIsDocumentVaultReadOnly();
  const hasAccessToThirdPartyShare = useHasAccessToThirdPartyShare();
  const isThirdPartyDocumentSharingEnabled = useFeatureFlag(
    Feature.EnableThirdPartyDocumentVaultSharing,
  );

  const canDownload =
    entity.type === EntityTypeDTO.Folder && entity.providerType === ProviderType.Internal;
  const canRename = !entity.isReadOnly && entity.isRenameable && !isDocumentVaultReadOnly;
  const canOrganize = !entity.isReadOnly && !isDocumentVaultReadOnly;
  const isExcludedFromThirdPartyShare =
    entity.providerType === ProviderType.External &&
    (entity.name.startsWith(ExternalFolder.FidelityDocuments) ||
      entity.name.startsWith(ExternalFolder.FidelityNFSDocuments));
  const canShare =
    isThirdPartyDocumentSharingEnabled &&
    hasAccessToThirdPartyShare &&
    !isExcludedFromThirdPartyShare &&
    !entity.isParentSharedWithThirdParty;
  const canDelete = !entity.isReadOnly;

  const hasMenuItems = canRename || canOrganize || canDownload || canShare || canDelete;

  const clickableEntityProps = {
    tabIndex: 0,
    role: 'button',
    ariaLabel: entity.name,
    onClick: onEntityClick,
    onKeyDown: evt => {
      if (evt.key === ' ' || evt.key === 'Enter') {
        onEntityClick();
      }
    },
  };

  return (
    <ClickableEntityStyle {...rest} {...clickableEntityProps}>
      <Box flex={ColumnWidths.Name} display="flex" alignItems="center">
        <EntityIcon entity={entity} mr="4" />

        <TruncatedText fontWeight={500}>{entity.name}</TruncatedText>
      </Box>

      <Box flex={ColumnWidths.LastModified}>
        {entity.lastModified && <Text>{entity.lastModified}</Text>}
      </Box>

      <Box flex={ColumnWidths.FileSize}>
        {entity.size != null && <Text>{formatFileSize(entity.size)}</Text>}
      </Box>

      <Box
        flex={ColumnWidths.Actions}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        {hasMenuItems && (
          <Box onClick={evt => evt.stopPropagation()}>
            <Menu>
              <MenuTrigger>
                <IconButton aria-label="Menu" icon="fas fa-ellipsis-h" variant="subtleInactive" />
              </MenuTrigger>

              <MenuContent>
                {canRename && (
                  <MenuItem onSelect={() => onRenameClick()}>
                    <ListItem icon="far fa-pen">Rename</ListItem>
                  </MenuItem>
                )}

                {canOrganize && (
                  <MenuItem onSelect={() => onOrganizeClick()}>
                    <ListItem icon="far fa-folder-tree">Organize</ListItem>
                  </MenuItem>
                )}

                {canDownload && (
                  <MenuItem onSelect={() => onEntityDownload()}>
                    <ListItem icon="far fa-arrow-circle-down">Download</ListItem>
                  </MenuItem>
                )}

                {canShare && (
                  <MenuItem onSelect={() => onThirdPartyShareClick?.()}>
                    <ListItem icon="far fa-user-friends">Share</ListItem>
                  </MenuItem>
                )}

                {canDelete && (
                  <MenuItem onSelect={() => onEntityDelete()}>
                    <ListItem icon="far fa-trash-alt">Delete</ListItem>
                  </MenuItem>
                )}
              </MenuContent>
            </Menu>
          </Box>
        )}
      </Box>
    </ClickableEntityStyle>
  );
};
